import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { taskKeys } from '@/features/tasks/useTasks';
import { CARRY_STATUSES, carryOverWindow } from './carryOver';
import type { CreateTimeBlockInput, TimeBlock, UpdateTimeBlockInput } from './types';

export const blockKeys = {
  all: ['time-blocks'] as const,
  range: (start: string, end: string) => ['time-blocks', 'range', start, end] as const,
  carry: (end: string) => ['time-blocks', 'carry', end] as const,
};

export function useTimeBlocks(range: { start: Date; end: Date }) {
  const start = range.start.toISOString();
  const end = range.end.toISOString();
  return useQuery({
    queryKey: blockKeys.range(start, end),
    queryFn: () =>
      api<TimeBlock[]>(
        `/api/time-blocks?ends_after=${encodeURIComponent(start)}` +
          `&starts_before=${encodeURIComponent(end)}`,
      ),
  });
}

/**
 * Unfinished blocks left behind before the day being planned.
 *
 * A separate query from the day's own blocks: the planner window moves as you
 * navigate, and this window trails behind it. Keyed under `blockKeys.all`, so
 * every mutation that invalidates blocks refreshes this list too.
 */
export function useCarryOverBlocks(dayStart: Date) {
  const { start, end } = carryOverWindow(dayStart);
  const after = start.toISOString();
  const before = end.toISOString();
  const statuses = CARRY_STATUSES.map((s) => `&status_in=${s}`).join('');
  return useQuery({
    queryKey: blockKeys.carry(before),
    queryFn: () =>
      api<TimeBlock[]>(
        `/api/time-blocks?ends_after=${encodeURIComponent(after)}` +
          `&starts_before=${encodeURIComponent(before)}${statuses}`,
      ),
  });
}

export function useCreateTimeBlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTimeBlockInput) =>
      api<TimeBlock>('/api/time-blocks', { method: 'POST', json: body }),
    onSuccess: (block) => {
      qc.invalidateQueries({ queryKey: blockKeys.all });
      // Task may have flipped to scheduled.
      qc.invalidateQueries({ queryKey: taskKeys.all });
      if (block.write_error) {
        toast.show(`Scheduled. Calendar write pending: ${block.write_error}`);
      }
    },
    onError: (err: Error) => toast.error(err.message || 'Could not schedule'),
  });
}

export function useUpdateTimeBlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: number } & UpdateTimeBlockInput) =>
      api<TimeBlock>(`/api/time-blocks/${id}`, { method: 'PATCH', json: patch }),
    onMutate: async ({ id, ...patch }) => {
      await qc.cancelQueries({ queryKey: blockKeys.all });
      const previous = qc.getQueriesData<TimeBlock[]>({ queryKey: blockKeys.all });
      qc.setQueriesData<TimeBlock[]>({ queryKey: blockKeys.all }, (old) =>
        old?.map((b) => (b.id === id ? { ...b, ...patch } : b)),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      ctx?.previous.forEach(([key, data]) => qc.setQueryData(key, data));
      toast.error('Could not move block');
    },
    onSettled: (_data, error) => {
      if (error) qc.invalidateQueries({ queryKey: blockKeys.all });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: blockKeys.all });
    },
  });
}

export function useDeleteTimeBlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api<void>(`/api/time-blocks/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: blockKeys.all });
      qc.invalidateQueries({ queryKey: taskKeys.all });
    },
    onError: () => toast.error('Could not return to backlog'),
  });
}

export function useConfirmTimeBlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api<TimeBlock>(`/api/time-blocks/${id}/confirm`, { method: 'POST' }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: blockKeys.all });
      qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}
